import { forwardRef } from 'react';
import { Link, LinkProps, useHistory, useLocation } from 'react-router-dom';

import { ButtonBase } from '@mui/material';
import { TextLink } from '@natter-co/design-system';
import { VISUAL_REFRESH } from 'common/utils';
import { ChevronLeftIcon } from 'icons';

type BackButtonProps =
  | {
      onClick?(): void;
      href?: never;
    }
  | {
      onClick?: never;
      href: string;
    };

const LinkComponent = forwardRef<HTMLAnchorElement, LinkProps>(({ to, ...props }, ref) => (
  <Link innerRef={ref} to={to} {...props} />
));

export const BackButton = ({ onClick, href }: BackButtonProps) => {
  const { pathname } = useLocation();
  const history = useHistory();
  const pathnames = pathname.split('/').filter(Boolean);

  const handleBackButtonClick = () => {
    history.push(`/${pathnames.slice(0, -1).join('/')}`);
  };

  // The visual-refresh gate lives here, in the shared component, not at its four call sites. Neutral,
  // flush with the heading below it, 14px / 500 as the old button was (a text link otherwise takes the
  // 16px / 400 of the page around it), with the word kept: an arrow alone would drop the label that older
  // and less technical readers rely on.
  if (VISUAL_REFRESH) {
    // A `render` target for `TextLink standalone`, which is the DS component; a button element is how it acts.
    // oxlint-disable-next-line react/forbid-elements
    const action = <button type="button" onClick={onClick ?? handleBackButtonClick} />;

    return (
      <TextLink
        standalone
        className="text-label-lg"
        startIcon=<ChevronLeftIcon />
        render={href ? <Link to={href} /> : action}
      >
        Back
      </TextLink>
    );
  }

  return (
    <ButtonBase
      disableRipple
      sx={({ typography }) => ({ ...typography['natter-text-sm'], fontWeight: 500, gap: 0.25 })}
      {...(href ? { LinkComponent, to: href } : { onClick: onClick ?? handleBackButtonClick })}
    >
      <ChevronLeftIcon fontSize="small" />
      Back
    </ButtonBase>
  );
};
